import { agentHandleFor, agentShortName, isReservedAgentRole } from "@baton/contracts";
import { useEffect, useMemo, useState } from "react";
import type { Agent, ProjectAgentAssignment } from "@baton/contracts";
import { ApiError, type CurrentUser, type DashboardApi } from "./api.js";
import { AGENT_ROLE_PRESETS, HEX_COLOR_PATTERN, roleColor, slugify } from "./agent-roles.js";
import { Modal } from "./components/modal.js";
import type { AgentCreateSeed } from "./flow/nodes.js";
import type { DecryptedProjectGraph } from "./projects-view.js";

const ICONS = ["agent", "build", "review", "merge", "test", "plan", "docs"] as const;
const HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Creates (or reuses) one of the viewer's agents and assigns it to the selected project,
 * optionally pre-wired to the agent the drag started from. Nothing runs on any machine.
 */
export function AgentCreateDialog(props: {
  open: boolean;
  onClose(): void;
  api: DashboardApi;
  user: CurrentUser;
  ownAgents: Agent[];
  graph: DecryptedProjectGraph;
  seed: AgentCreateSeed | null;
  onCreated(agent: Agent, assignment: ProjectAgentAssignment): Promise<void> | void;
}) {
  const [role, setRole] = useState("implementer");
  const [customRole, setCustomRole] = useState("");
  const [handle, setHandle] = useState("");
  const [handleTouched, setHandleTouched] = useState(false);
  const [icon, setIcon] = useState<string>("build");
  const [color, setColor] = useState("#2563eb");
  const [repositoryId, setRepositoryId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const effectiveRole = role === "custom" ? slugify(customRole) : role;
  const suggestedHandle = effectiveRole ? `${props.user.handle}-${effectiveRole}` : props.user.handle;
  const existing = useMemo(
    () => props.ownAgents.find((agent) => agent.handle === (handleTouched ? handle : suggestedHandle) && !agent.retiredAt) ?? null,
    [props.ownAgents, handle, handleTouched, suggestedHandle],
  );

  useEffect(() => {
    if (!props.open) return;
    setError(null); setWorking(false); setHandleTouched(false);
    setRepositoryId(props.seed?.repositoryId ?? "");
  }, [props.open, props.seed]);

  useEffect(() => {
    if (!handleTouched) setHandle(suggestedHandle);
  }, [suggestedHandle, handleTouched]);

  function pickRole(next: string) {
    setRole(next);
    const preset = AGENT_ROLE_PRESETS.find((candidate) => candidate.role === next);
    setIcon(preset?.icon ?? "agent");
    setColor(preset?.color ?? roleColor(next === "custom" ? slugify(customRole) || "custom" : next));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!effectiveRole || !HANDLE_PATTERN.test(handle)) {
      setError("Role and name must be lowercase slugs such as planner or security-review.");
      return;
    }
    // Your general agent is already shown and addressed as "general"; the API refuses a second one.
    if (isReservedAgentRole(effectiveRole)) {
      setError("\"general\" already names your general agent; pick another role.");
      return;
    }
    setWorking(true); setError(null);
    try {
      const safeColor = HEX_COLOR_PATTERN.test(color) ? color.toLowerCase() : roleColor(effectiveRole);
      const agent = existing ?? await props.api.createAgent({ handle, role: effectiveRole, icon, color: safeColor });
      const assignment = await props.api.assignProjectAgent(props.graph.project.id, agent.id, repositoryId || null);
      if (props.seed?.wireFromAgentId && props.seed.wireFromAgentId !== agent.id) {
        await props.api.createWire(props.graph.project.id, {
          id: crypto.randomUUID(),
          sourceAgentId: props.seed.wireFromAgentId,
          targetAgentId: agent.id,
        }).catch(() => undefined);
      }
      await props.onCreated(agent, assignment);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "agent_handle_taken") {
        const suggestion = (caught.details as { suggestion?: string } | undefined)?.suggestion;
        if (suggestion) { setHandle(suggestion); setHandleTouched(true); }
        setError(`That handle is taken team-wide${suggestion ? `; try ${suggestion}` : ""}.`);
      } else if (caught instanceof ApiError && caught.code === "agent_role_reserved") {
        setError("\"general\" already names your general agent; pick another role.");
      } else if (caught instanceof ApiError && caught.code === "agent_role_taken") {
        setError("You already have an agent with this role; pick it under Assign existing, or choose another role.");
      } else {
        setError(caught instanceof Error ? caught.message : "Could not create the agent");
      }
    } finally {
      setWorking(false);
    }
  }

  return <Modal open={props.open} onClose={props.onClose} title="New agent">
    <form className="agent-create" onSubmit={submit} data-testid="agent-create-dialog">
      <p className="muted">An agent is a durable identity you own. It joins <strong>{props.graph.project.name}</strong> now; connecting Claude Code or Codex to it is a command you run yourself afterwards.</p>
      <fieldset className="agent-create-roles">
        <legend>Role</legend>
        <div className="agent-presets" role="radiogroup" aria-label="Role">
          {AGENT_ROLE_PRESETS.map((preset) => <button
            type="button"
            key={preset.role}
            role="radio"
            aria-checked={role === preset.role}
            className={role === preset.role ? "active" : ""}
            style={{ "--preset-accent": preset.color } as React.CSSProperties}
            onClick={() => pickRole(preset.role)}
          >{preset.label}<small>{preset.hint}</small></button>)}
          <button type="button" role="radio" aria-checked={role === "custom"} className={role === "custom" ? "active" : ""} onClick={() => pickRole("custom")}>Custom<small>your own role slug</small></button>
        </div>
        {role === "custom" && <label>Custom role<input value={customRole} onChange={(event) => { const next = slugify(event.target.value); setCustomRole(next); setColor(roleColor(next || "custom")); }} placeholder="e.g. security-review" required /></label>}
      </fieldset>
      <label>Name
        <input value={agentShortName({ handle, role: effectiveRole || "custom" }, props.user.handle)} onChange={(event) => { setHandleTouched(true); setHandle(agentHandleFor(props.user.handle, slugify(event.target.value))); }} required maxLength={40} pattern="[a-z0-9]+(-[a-z0-9]+)*" />
      </label>
      <p className="muted compact">Address <code>@{props.user.handle}/{agentShortName({ handle, role: effectiveRole || "custom" }, props.user.handle)}</code>. You are the owner; the name is the agent. Stored handle: <code>{handle}</code>.</p>
      {existing && <p className="banner">You already own <strong>{agentShortName(existing, props.user.handle)}</strong>; it will be assigned instead of creating a duplicate.</p>}
      <div className="field-row">
        <label>Icon
          <select value={icon} onChange={(event) => setIcon(event.target.value)}>
            {ICONS.map((candidate) => <option key={candidate} value={candidate}>{candidate}</option>)}
          </select>
        </label>
        <label>Colour
          <input type="color" value={color} onChange={(event) => setColor(event.target.value.toLowerCase())} />
        </label>
      </div>
      <label>Scope
        <select value={repositoryId} onChange={(event) => setRepositoryId(event.target.value)}>
          <option value="">Whole project</option>
          {props.graph.repositories.map((repository) => <option key={repository.id} value={repository.id}>{repository.ownerHandle}/{repository.name}</option>)}
        </select>
      </label>
      {props.seed?.wireFromAgentId && <p className="muted compact">A conversation wire will connect it to the agent you dragged from.</p>}
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="secondary" onClick={props.onClose} disabled={working}>Cancel</button>
        <button type="submit" className="primary" disabled={working}>{working ? "Creating…" : existing ? "Assign agent" : "Create agent"}</button>
      </div>
    </form>
  </Modal>;
}
